<template>
    <Panel
        v-if="$route.meta.isCabinet"
        :title="title"
        ref="panel"
    />
	<router-view v-slot="{ Component }">
		<component ref="view" :is="Component" @setPageTitle="setPageTitle" />
	</router-view>
    <notifications position="bottom right" width="400px">
        <template #body="props">
            <div class="app-notification" :class="props.item.type">
                <div class="d-flex align-items-center gap-3">
                    <i v-if="props.item.type" class="bi fs-4" :class="{
                        'bi-x-circle-fill': props.item.type==='error',
                        'bi-check-circle-fill': props.item.type==='success',
                        'bi-exclamation-triangle-fill': props.item.type==='warn'
                    }"></i>
                    <div class="flex-grow-1">
                        <h6 v-if="props.item.title" class="mb-0">{{ props.item.title }}</h6>
                        <div v-html="props.item.text"/>
                    </div>
                    <button class="btn btn-sm btn-link p-0" @click="props.close">
                        <i class="bi bi-x-lg"></i>
                    </button>
                </div>
            </div>
        </template>
    </notifications>
</template>

<script>
import Panel from './components/Panel.vue'

export default {
	name: 'App',
    data:() => ({ title: '' }),
	components: { Panel },
    methods: {
        setPageTitle(title) {
            this.title = title
        }
    }
}
</script>

<style>
@import url("https://cdn.jsdelivr.net/npm/bootstrap-icons@1.13.1/font/bootstrap-icons.min.css");
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

:root {
    --primary-color: #6d3cf7;
    --primary-color-rgb: 109, 60, 247;
    --main-gradient: linear-gradient(135deg, #6d3cf7 0%, #b44ea8 50%, #ff9f22 100%);
    --default-transition: .2s ease;
    --border-radius: 16px;
    --border-radius-sm: 10px;
    --border-radius-lg: 20px;
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06);
    --shadow-md: 0 4px 6px rgba(0,0,0,0.04), 0 2px 4px rgba(0,0,0,0.06);
    --shadow-lg: 0 10px 25px rgba(0,0,0,0.06), 0 4px 10px rgba(0,0,0,0.04);
    --bg-page: #f5f6fa;
    --bg-card: #ffffff;
    --text-primary: #1a1d26;
    --text-secondary: #6b7280;
    --text-muted: #9ca3af;
    --border-color: #e5e7eb;
}

* {
    box-sizing: border-box;
}

#app {
	font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
    color: var(--text-primary);
    font-size: 14px;
    line-height: 1.5;
}

body {
    margin: 0;
    background-color: var(--bg-page);
}

#app hr {
    height: 3px;
    background-image: var(--main-gradient);
    border-radius: var(--border-radius);
    border: 0;
    opacity: 1;
}

/* ── Layout ─────────────────────────────────── */
#content {
    margin-left: 260px;
    padding: 100px 2em 2em;
    min-height: 100vh;
    background-color: var(--bg-page);
}

/* ── Typography ─────────────────────────────── */
h1, h2, h3, h4, h5, h6 { font-weight: 600; }

.text-gradient {
    color: transparent !important;
    background-image: var(--main-gradient) !important;
    background-clip: text !important;
    -webkit-background-clip: text !important;
    -webkit-text-fill-color: transparent !important;
}

/* ── Cards ──────────────────────────────────── */
#app .card-content {
    padding: 2em;
    border: 0;
    border-radius: var(--border-radius);
    box-shadow: var(--shadow-sm);
    background: var(--bg-card);
    width: fit-content;
}

#app .card-edit {
    height: 36px !important;
    width: 36px !important;
    border-radius: 50% !important;
    color: var(--text-muted) !important;
    transition: var(--default-transition);
}

#app .card-edit:hover {
    color: var(--text-primary) !important;
    background-color: var(--bg-page);
}

#app .card-footer {
    background-color: rgb(188, 121, 212) !important;
    color: rgb(248, 248, 248) !important;
}

/* ── Buttons ────────────────────────────────── */
#app .btn {
    font-weight: 500;
    font-size: 13px;
    transition: var(--default-transition);
}

#app .btn-primary {
    background-image: var(--main-gradient) !important;
    border: none !important;
    color: #fff;
    padding: 0.5em 1.5em;
}

#app .btn-primary:hover {
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(109, 60, 247, 0.3);
}

#app .btn-primary,
#app .btn-danger,
#app .btn-outline-primary,
#app .btn-outline-secondary,
#app .btn-outline-danger {
    border-radius: var(--border-radius-sm);
}

#app .btn-outline-primary {
    color: var(--primary-color);
    border-color: var(--primary-color);
}

#app .btn-outline-primary:hover {
    background-color: var(--primary-color);
    border-color: var(--primary-color);
    color: #fff;
}

#app .btn-link {
    text-decoration: none;
    color: var(--text-secondary);
    padding-left: 0;
}

#app .btn-link:hover {
    color: var(--primary-color);
}

/* ── Forms ──────────────────────────────────── */
#app .form-control,
#app .form-select {
    border-radius: var(--border-radius-sm);
    border-color: var(--border-color);
    font-size: 13px;
    padding: 0.55em 0.85em;
    transition: var(--default-transition);
}

#app .form-control:focus,
#app .form-select:focus {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.1);
}

#app .form-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

#app .form-switch .form-check-input {
    background-color: #e9ecef;
    border-color: #dee2e6;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='rgba(255,255,255,1)'/%3e%3c/svg%3e");
}

#app .form-check-input:checked {
    background-color: transparent;
    background-image: var(--main-gradient) !important;
}

#app .form-switch .form-check-input:checked {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='rgba(255,255,255,1)'/%3e%3c/svg%3e"),
                      var(--main-gradient) !important;
}

#app .form-check-input:checked {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/%3e%3c/svg%3e"),
                      var(--main-gradient) !important;
}

/* ── Accordion ──────────────────────────────── */
#app .accordion-button:not(.collapsed) {
    background-color: transparent !important;
    color: black !important;
}
#app .accordion-button:focus {
    box-shadow: none !important;
}

/* ── Navbar / Sidebar ───────────────────────── */
#app .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: 260px;
    background-color: var(--bg-card);
    border-right: 1px solid var(--border-color);
    z-index: 10;
}

#app .navbar {
    background-color: var(--bg-card);
}

#app .cabinet-header {
    position: fixed;
    top: 0;
    left: 260px;
    right: 0;
    background-color: var(--bg-card);
    border-bottom: 1px solid var(--border-color);
    z-index: 9;
}

/* ── Plans Table ────────────────────────────── */
#app .plans-table td {
    text-align: center;
    vertical-align: middle;
}

#app .plans-table th {
    font-weight: 500;
}

#app .plans-table .table-header {
    height: 200px;
}

#app .table-module th,
#app .table-module td {
    background-color: #fff !important;
}

#app .plans-table > tbody > tr:nth-of-type(2n) > * {
    background-color: #f9f5ff;
}
#app .plans-table > tbody > tr:nth-of-type(2n) > *:not(:last-child) {
    border-right: 3px solid white;
}
#app .plans-table > tbody > tr:nth-of-type(2n+1) > * {
    background-color: white !important;
}

#app .table-header th {
    border-radius: 20px 20px 0 0;
    border-left-width: 3px;
    border-right-width: 3px;
    border-color: var(--bg-page);
}

#app .top-left{ border-top-left-radius: 20px;}
#app .top-right{ border-top-right-radius: 20px;}
#app .bottom-left{ border-bottom-left-radius: 20px;}
#app .bottom-right{ border-bottom-right-radius: 20px;}

/* ── Plans Cards ────────────────────────────── */
#adminPlansList .plan-card { min-height: 500px; }

#app .add-plan-card {
    height: 500px !important;
    border: 3px dashed var(--border-color) !important;
    transition: var(--default-transition);
}

#app .add-plan-card:hover {
    border-color: var(--primary-color) !important;
}

/* ── Misc ───────────────────────────────────── */
#app .card .stretched-link { cursor: pointer; }

#app .nav-link { }

#callCenterPrice::after {
    margin: 2px !important;
    content: "tg" !important;
}

#app .empty-cell { background-color: transparent !important; }

/* ── Notifications ──────────────────────────── */
#app .app-notification {
    background-color: var(--bg-card);
    border-radius: var(--border-radius-sm);
    padding: 1rem 1.25rem;
    margin: 0.75rem;
    box-shadow: var(--shadow-lg);
    border-left: 4px solid var(--border-color);
}

#app .app-notification.success {
    border-left-color: #10b981;
}
#app .app-notification.success .bi { color: #10b981; }

#app .app-notification.error {
    border-left-color: #ef4444;
}
#app .app-notification.error .bi { color: #ef4444; }

#app .app-notification.warn {
    border-left-color: #f59e0b;
}
#app .app-notification.warn .bi { color: #f59e0b; }

/* ── Loading Spinner ────────────────────────── */
.loading::after {
    content: '\F116';
    display: inline-block;
    margin: -.56rem .5rem;
    color: var(--primary-color);
    font-family: bootstrap-icons;
    font-size: 1.5rem;
    animation: loading 0.8s linear 0s infinite;
}

.modal-footer .loading::after {
    font-size: 1em;
}

@keyframes loading {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

/* ── Responsive ─────────────────────────────── */
@media (max-width: 768px) {
    #app #content {
        margin-left: 0;
        padding: 80px 1em 6em;
    }

    #app .cabinet-header {
        left: 0;
    }

    #app .brand {
        width: auto !important;
    }

    #app .navbar .container-fluid {
        padding-left: 0;
        padding-right: 0;
    }

    #app .sidebar {
        display: none;
    }
}
</style>
